import queryClient from '@/services/queryClient'

import {
  handleFeatureFlagBroadcast,
  handleOptimizationJobsBroadcast,
  invalidateNotificationQueries,
} from './broadcastHandlers'

/**
 * Cache freshness in KBE is push-only, and Realtime broadcasts are *dropped*
 * rather than queued while a channel is not SUBSCRIBED. Combined with the
 * global `staleTime: Infinity`, anything published inside such a window is
 * never re-read: the write is in Postgres, the UI shows the value from before
 * it, and no amount of waiting fixes that (KBE-770).
 *
 * There are three such windows, none of them rare:
 *  - initial load — channels only `.subscribe()` once the first `getTimestamps`
 *    fetch resolves (`useLastModifications`), and the join is another round
 *    trip after that, so a write made right after the page becomes interactive
 *    lands in the gap;
 *  - a socket drop and rejoin (the Realtime tenant cold-stops when idle, and
 *    a loaded host times joins out — see SUSTAINED_FAILURE_THRESHOLD);
 *  - the deliberate teardown/re-subscribe on SIGNED_IN / TOKEN_REFRESHED.
 *
 * The only existing catch-up is `useLastModifications`' `queryFn` on
 * window-focus/reconnect, which covers a researcher switching tabs but fires
 * neither in a Playwright run nor for a laptop that wakes on the same tab.
 *
 * So treat every SUBSCRIBED as "I may have just missed something" and re-read
 * the timestamp tables: `useLastModifications`' `queryFn` diffs the fresh
 * timestamps against the cached ones and invalidates exactly the queries a
 * missed broadcast would have. The direct-table topics have no timestamp row
 * to diff against, so their lists are invalidated outright.
 */

// Channels subscribe as a batch over one socket, so their SUBSCRIBED callbacks
// arrive within a few milliseconds of each other. Coalescing turns those into
// a single re-read instead of one per topic.
const RESYNC_COALESCE_MS = 100

let pendingResync: ReturnType<typeof setTimeout> | undefined

function resync(): void {
  // Active only: an inactive `lastModification` query has `staleTime: 0`, so
  // it refetches by itself the moment something mounts it again.
  void queryClient.refetchQueries({
    queryKey: ['lastModification'],
    type: 'active',
  })

  handleOptimizationJobsBroadcast()
  handleFeatureFlagBroadcast()
  invalidateNotificationQueries()
}

export function requestRealtimeResync(): void {
  if (pendingResync !== undefined) return
  pendingResync = setTimeout(() => {
    pendingResync = undefined
    resync()
  }, RESYNC_COALESCE_MS)
}
